/**
 * Tela de auditoria — /configuracoes/acessos/auditoria.
 *
 * Lista paginada de eventos com filtros (ator, alvo, acao, resultado,
 * periodo). Click numa linha abre slide-over com diff antes/depois.
 *
 * P12: drawer em vez de modal (preserva contexto da tabela).
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiFilter, FiRefreshCw } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";

import AuditTimeline from "../../../components/AuditTimeline";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { usePersistedFilters } from "../../../hooks/usePersistedFilters";

import { fetchAuditoria, fetchAuditoriaEvento } from "../api";
import {
  ActionsBar,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SearchBar,
  SecondaryButton,
  Select,
} from "../styles.jsx";

const AUDITORIA_FILTROS_KEY = "pharuerp:acessos:auditoria:filtros";
const AUDITORIA_FILTROS_DEFAULTS = {
  acao: "",
  resultado: "",
  alvo_tipo: "",
  alvo_id: "",
  ator: "",
};

function AuditoriaPagina() {
  const { request } = useAuth();
  const [searchParams] = useSearchParams();
  const { filtros, aplicar, limpar, temAlgumAtivo } = usePersistedFilters(
    AUDITORIA_FILTROS_KEY,
    AUDITORIA_FILTROS_DEFAULTS
  );
  const [estado, setEstado] = useState({ loading: true, eventos: [], total: 0, error: null });
  const [eventoSelecionado, setEventoSelecionado] = useState(null);

  const carregar = useCallback(async () => {
    setEstado((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const payload = await fetchAuditoria(request, { ...filtros, por_pagina: 50 });
      setEstado({
        loading: false,
        eventos: payload?.resultados || [],
        total: payload?.total || 0,
        error: null,
      });
    } catch (err) {
      setEstado({
        loading: false,
        eventos: [],
        total: 0,
        error: err?.message || "Erro ao carregar auditoria.",
      });
    }
  }, [request, filtros]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Suporta abrir direto via ?evento=:id
  useEffect(() => {
    const eventoQs = searchParams.get("evento");
    if (eventoQs) {
      fetchAuditoriaEvento(request, eventoQs)
        .then((ev) => setEventoSelecionado(ev))
        .catch(() => setEventoSelecionado(null));
    }
  }, [request, searchParams]);

  const abrirDetalhe = async (id) => {
    try {
      const ev = await fetchAuditoriaEvento(request, id);
      setEventoSelecionado(ev);
    } catch (err) {
      setEstado((p) => ({ ...p, error: err?.message || "Erro ao carregar evento." }));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Auditoria
          {estado.total > 0 && (
            <small style={{ opacity: 0.6, marginLeft: 8, fontWeight: 400 }}>
              {estado.total} eventos
            </small>
          )}
        </CardTitle>
        <ActionsBar>
          <SecondaryButton type="button" onClick={carregar} disabled={estado.loading}>
            <FiRefreshCw size={14} /> Atualizar
          </SecondaryButton>
        </ActionsBar>
      </CardHeader>

      <ActionsBar>
        <SearchBar
          type="text"
          placeholder="Filtrar por ação (ex.: login, permissão)..."
          value={filtros.acao}
          onChange={(e) => aplicar("acao", e.target.value)}
          aria-label="Filtrar por ação"
        />
        <Select
          value={filtros.resultado}
          onChange={(e) => aplicar("resultado", e.target.value)}
          aria-label="Filtrar por resultado"
        >
          <option value="">Qualquer resultado</option>
          <option value="ok">OK</option>
          <option value="falha">Falha</option>
          <option value="bloqueado">Bloqueado</option>
        </Select>
        <Select
          value={filtros.alvo_tipo}
          onChange={(e) => aplicar("alvo_tipo", e.target.value)}
          aria-label="Filtrar por tipo do alvo"
        >
          <option value="">Qualquer alvo</option>
          <option value="usuario">Usuário</option>
          <option value="cargo">Cargo</option>
          <option value="permissao">Permissão</option>
        </Select>
        {temAlgumAtivo && (
          <SecondaryButton type="button" onClick={limpar}>
            <FiFilter size={14} /> Limpar
          </SecondaryButton>
        )}
      </ActionsBar>

      {estado.error && <ErrorMsg>{estado.error}</ErrorMsg>}
      {estado.loading ? (
        <EmptyMsg>Carregando...</EmptyMsg>
      ) : !estado.eventos.length ? (
        <EmptyMsg>Nenhum evento com esses filtros.</EmptyMsg>
      ) : (
        <AuditTimeline events={estado.eventos} onEventClick={abrirDetalhe} />
      )}

      <SlideOverPanel
        open={Boolean(eventoSelecionado)}
        onClose={() => setEventoSelecionado(null)}
        title="Detalhe do evento"
        subtitle={
          eventoSelecionado
            ? new Date(eventoSelecionado.timestamp).toLocaleString("pt-BR")
            : null
        }
        width="min(95vw, 560px)"
      >
        {eventoSelecionado && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <ActionsBar>
              <Pill $tone="info">{eventoSelecionado.acao}</Pill>
              <Pill
                $tone={
                  eventoSelecionado.resultado === "ok" ? "success" : "danger"
                }
              >
                {eventoSelecionado.resultado}
              </Pill>
            </ActionsBar>

            <Card>
              <CardHeader>
                <CardTitle>Contexto</CardTitle>
              </CardHeader>
              <CardSubtitle>
                <strong>Ator:</strong> {eventoSelecionado.ator_email || "anon"}
              </CardSubtitle>
              {eventoSelecionado.alvo_label && (
                <CardSubtitle>
                  <strong>Alvo:</strong> {eventoSelecionado.alvo_label}
                  {eventoSelecionado.alvo_tipo && ` (${eventoSelecionado.alvo_tipo})`}
                </CardSubtitle>
              )}
              {eventoSelecionado.ip && (
                <CardSubtitle>
                  <strong>IP:</strong> {eventoSelecionado.ip}
                </CardSubtitle>
              )}
              {eventoSelecionado.user_agent && (
                <CardSubtitle>
                  <strong>UA:</strong> {eventoSelecionado.user_agent}
                </CardSubtitle>
              )}
              {eventoSelecionado.request_id && (
                <CardSubtitle>
                  <strong>Request ID:</strong> {eventoSelecionado.request_id}
                </CardSubtitle>
              )}
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Antes</CardTitle>
              </CardHeader>
              <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(eventoSelecionado.antes || {}, null, 2)}
              </pre>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Depois</CardTitle>
              </CardHeader>
              <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(eventoSelecionado.depois || {}, null, 2)}
              </pre>
            </Card>
          </div>
        )}
      </SlideOverPanel>
    </Card>
  );
}

export default AuditoriaPagina;
